import { router, useFocusEffect } from "expo-router";
import { useCallback, useMemo, useRef, useState } from "react";
import { Platform, View } from "react-native";

import Button from "$src/core/components/atoms/Button/Button";
import Image from "$src/core/components/atoms/Image/Image";
import LazyRender from "$src/core/components/atoms/LazyRender/LazyRender";
import { AnimatedScrollView } from "$src/core/components/atoms/ScrollView/ScrollView";
import Text from "$src/core/components/atoms/Text/Text";
import HeroGradientMaskedImage from "$src/core/components/molecules/HeroGradientMaskedImage";
import PlayContentButton from "$src/core/components/molecules/PlayContentButton";
import EpisodeBrowser from "$src/core/components/organisms/EpisodeBrowser";
import type { RightAction } from "$src/core/components/organisms/StackHeader/StackHeader";
import StackHeader from "$src/core/components/organisms/StackHeader/StackHeader";
import useAnalytics from "$src/core/hooks/use-analytics";
import useAuth from "$src/core/hooks/use-auth";
import useConfig from "$src/core/hooks/use-config";
import useResponsiveImage from "$src/core/hooks/use-responsive-image";
import useScroll from "$src/core/hooks/use-scroll";
import useStackHeader from "$src/core/hooks/use-stack-header";
import useTranslation from "$src/core/hooks/use-translation";
import useWatchlist from "$src/core/hooks/use-watchlist";
import ErrorPage from "$src/core/pages/ErrorPage/ErrorPage";
import LoadingPage from "$src/core/pages/LoadingPage/LoadingPage";
import useContentQuery from "$src/core/queries/use-content-query";
import Alerting from "$src/core/services/alerting";
import Sharing from "$src/core/services/sharing";
import $pageInsets from "$src/core/styles/page";
import pickImageUrl from "$src/core/utils/image/pick-image-url";

import $, { IMAGE_DIMENSIONS } from "./DetailPage.styles";
import type { DetailPageProps } from "./types";

const DetailPage = ({ id, type }: DetailPageProps) => {
  const { t, d } = useTranslation();
  const { scrollToTop } = useScroll();
  const { linking } = useConfig();
  const { status: authStatus } = useAuth();
  const { toggle: toggleWatchlistItem, isWatchlisted } = useWatchlist();
  const { trackEvent } = useAnalytics();

  const { data, status, error, refetch } = useContentQuery({ id, type });

  const [isWatchlistLoading, setIsWatchlistLoading] = useState(false);

  const rightActions = useMemo(() => {
    const actions: RightAction[] = [];

    if (Sharing?.isAvailable()) {
      actions.push({
        name: "share",
        icon: "share",
        title: t("COMMON/SHARE"),
        onPress: () =>
          Sharing?.share({
            url: `${linking.root}/${type}/${id}`,
          }),
      });
    }

    if (data?.type === "episodes" && data.seriesId) {
      actions.push({
        name: "to_series",
        icon: "movie",
        title: t("DETAIL/TO_SERIES"),
        onPress: () => router.navigate(`/series/${data.seriesId}`),
      });
    }
    return actions;
  }, [data, linking.root, t, type, id]);

  const title = data?.title ?? data?.subtitle ?? "";

  useStackHeader({
    left: "back",
    right: rightActions,
  });

  const handleWatchlist = async () => {
    if (authStatus !== "authenticated") {
      Alerting.error(t("ERROR/AUTH/LOGIN_REQUIRED"));
      return;
    }

    try {
      if (!data) {
        throw new Error("No data found");
      }
      setIsWatchlistLoading(true);
      await toggleWatchlistItem(data.id);
      trackEvent(
        isWatchlisted(data.id) ? "watchlist_remove" : "watchlist_add",
        {
          content_id: data.id,
          content_type: type,
          content_title: data.title,
        },
      );
    } catch {
      Alerting.error(t("ERROR/WATCHLIST/TOGGLE"));
    } finally {
      setIsWatchlistLoading(false);
    }
  };

  const lastScrolledIdRef = useRef<string | null>(null);
  useFocusEffect(
    useCallback(() => {
      if (lastScrolledIdRef.current !== id) {
        lastScrolledIdRef.current = id;
        scrollToTop();
      }
    }, [id, scrollToTop]),
  );

  const cover = useMemo(() => {
    const image = data?.image ?? data?.images;
    return {
      uri: image ? pickImageUrl(image, ["landscape", "portrait"]) : undefined,
      dimensions: IMAGE_DIMENSIONS.cover,
    };
  }, [data?.image, data?.images]);

  const responsiveCoverImage = useResponsiveImage(cover.uri, {
    dimensions: cover.dimensions,
  });

  const poster = useMemo(() => {
    const image = data?.image ?? data?.images;
    return {
      uri: image ? pickImageUrl(image, ["portrait", "landscape"]) : undefined,
      dimensions: IMAGE_DIMENSIONS.poster,
    };
  }, [data?.image, data?.images]);

  const responsivePosterImage = useResponsiveImage(poster.uri, {
    dimensions: poster.dimensions,
  });

  const hasTrailer = !!data?.trailer;
  const hasPlayback = !!data?.playback;
  const hasSeasonsWithEpisodes = useMemo(() => {
    if (type !== "series" || !data || !("seasons" in data)) return false;
    try {
      return (
        data.seasons?.some((season) => (season.episodes?.length ?? 0) > 0) ??
        false
      );
    } catch {
      return false;
    }
  }, [data, type]);

  if (status === "pending") {
    return <LoadingPage />;
  }

  if (status === "error") {
    return (
      <ErrorPage
        title={t("ERROR/DETAIL_PAGE/TITLE")}
        subtitle={t("ERROR/DETAIL_PAGE/SUBTITLE")}
        ctaText={t("COMMON/RETRY")}
        ctaIcon="retry"
        onPressCta={refetch}
        error={error}
      />
    );
  }

  return (
    <AnimatedScrollView
      contentContainerStyle={$.container}
      accessibilityRole="list"
      showsVerticalScrollIndicator={false}
      scrollEventThrottle={16}
    >
      {Platform.OS === "web" && (
        <StackHeader left="back" right={rightActions} />
      )}

      {responsiveCoverImage && (
        <LazyRender delay={200}>
          <HeroGradientMaskedImage
            image={responsiveCoverImage}
            style={$.imageContainer}
          />
        </LazyRender>
      )}

      <View style={[$.content, $pageInsets.horizontal]}>
        <View style={$.meta}>
          {responsivePosterImage && (
            // This is a decorative image, so we don't need an accessibility label
            // eslint-disable-next-line triple-rn-a11y/accessibility-label
            <Image
              accessibilityRole="none"
              contentFit="cover"
              source={responsivePosterImage}
              style={$.metaPoster}
            />
          )}
          <View style={$.metaDetails}>
            <Text
              preset="heading1"
              numberOfLines={2}
              accessibilityRole="header"
              accessibilityLabel={t("DETAIL/TITLE/LABEL", {
                title,
              })}
            >
              {title}
            </Text>

            {!!data.tags?.length && (
              <Text
                preset="span"
                numberOfLines={1}
                accessibilityRole="text"
                accessibilityLabel={t("DETAIL/CATEGORIES/ACCESSIBILITY_LABEL", {
                  categories: data.tags.join(", "),
                })}
              >
                {data.tags.join(" · ")}
              </Text>
            )}
            {data.publishDate && (
              <Text
                preset="span"
                numberOfLines={1}
                accessibilityRole="text"
                accessibilityLabel={t("DETAIL/PUBLISH_DATE/LABEL", {
                  date: data.publishDate,
                })}
              >
                {d(data.publishDate)}
              </Text>
            )}
          </View>
        </View>

        <View style={$.actions}>
          {hasPlayback && (
            <PlayContentButton
              name="detail_page_play"
              contentId={id}
              contentType={type}
              contentTitle={data.title ?? ""}
              preset="primary"
            />
          )}
          {hasTrailer && (
            <PlayContentButton
              name="detail_page_trailer"
              contentId={id}
              contentType={type}
              contentTitle={data.title ?? ""}
              trailer
              preset={hasPlayback ? "secondary" : "primary"}
            />
          )}
          {isWatchlisted(data.id) ? (
            <Button
              name="detail_page_watchlist_remove"
              preset="secondary"
              label={t("DETAIL/WATCHLIST/REMOVE")}
              icon="check"
              onPress={handleWatchlist}
              isLoading={isWatchlistLoading}
              accessibilityLabel={t(
                "DETAIL/WATCHLIST/REMOVE/ACCESSIBILITY_LABEL",
              )}
            />
          ) : (
            <Button
              name="detail_page_watchlist_add"
              preset="secondary"
              label={t("DETAIL/WATCHLIST/ADD")}
              icon="plus"
              onPress={handleWatchlist}
              isLoading={isWatchlistLoading}
              accessibilityLabel={t("DETAIL/WATCHLIST/ADD/ACCESSIBILITY_LABEL")}
            />
          )}
        </View>

        <Text
          preset="body"
          accessibilityRole={Platform.OS === "web" ? "text" : "summary"}
        >
          {data.longDescription}
        </Text>
      </View>

      {hasSeasonsWithEpisodes && "seasons" in data && data.seasons && (
        <View style={[$.episodes, $pageInsets.horizontal]}>
          <EpisodeBrowser seasons={data.seasons} />
        </View>
      )}
    </AnimatedScrollView>
  );
};

export default DetailPage;
